import {
  Add,
  AddCircleOutline,
  ArrowDownward,
  ArrowDropDownCircle,
  ArrowUpward,
  CalendarToday,
  CheckBox,
  ContentCopy,
  DeleteOutline,
  Email,
  ExpandLess,
  ExpandMore,
  FilterList,
  MiscellaneousServices,
  Notes,
  Numbers,
  Phone,
  RadioButtonChecked,
  Sync,
  TextFields,
  Tune,
  VerifiedUser,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { type ElementType, useEffect, useState } from 'react';

import {
  CustomerIntakeField,
  CustomerIntakeFieldOption,
  CustomerIntakeFieldType,
  CustomerIntakeFormConfig,
  CustomerIntakeServiceBinding,
  CustomerIntakeServiceConfig,
  CustomerIntakeServiceLevel,
  CustomerIntakeServiceOptionNode,
  CustomerIntakeSection,
} from '../../types/CustomerIntake.type';
import { ServiceType } from '../../types/ServiceAccordion.type';
import { listServiceTree } from '../../utils/serviceFormConfigurationApi';

export type CustomerIntakeEditorMode = 'form-version' | 'package';

export type CustomerIntakeCustomTypeTarget = {
  sectionId: string;
  fieldId?: string;
};

export const CUSTOMER_INTAKE_FIELD_TYPE_OPTIONS: Array<{
  value: CustomerIntakeFieldType;
  label: string;
  Icon: ElementType;
}> = [
  { value: 'text', label: 'Short text', Icon: TextFields },
  { value: 'textarea', label: 'Long text', Icon: Notes },
  { value: 'phone', label: 'Phone number', Icon: Phone },
  { value: 'email', label: 'Email', Icon: Email },
  { value: 'date', label: 'Date', Icon: CalendarToday },
  { value: 'number', label: 'Number', Icon: Numbers },
  { value: 'radio', label: 'Radio choice', Icon: RadioButtonChecked },
  { value: 'checkbox', label: 'Checkboxes', Icon: CheckBox },
  { value: 'select', label: 'Dropdown', Icon: ArrowDropDownCircle },
  { value: 'service', label: 'Service', Icon: MiscellaneousServices },
  { value: 'consent', label: 'Consent checkbox', Icon: VerifiedUser },
];

export const CUSTOMER_INTAKE_SERVICE_LEVEL_OPTIONS: Array<{
  value: CustomerIntakeServiceLevel;
  label: string;
}> = [
  { value: 'serviceType', label: 'Service type' },
  { value: 'subtype', label: 'Sub-type' },
  { value: 'package', label: 'Service' },
];

const createDefaultServiceConfig = (
  options: CustomerIntakeServiceOptionNode[] = [],
): CustomerIntakeServiceConfig => ({
  mode: 'full',
  multiple: false,
  options,
});

/** Count the leaf-most option nodes captured in a snapshot, for display. */
const countServiceOptionNodes = (
  options: CustomerIntakeServiceOptionNode[] = [],
): number =>
  options.reduce((total, serviceType) => {
    const subtypes = serviceType.subtypes || [];
    const packages = subtypes.reduce(
      (sum, subtype) => sum + (subtype.packages?.length || 0),
      0,
    );
    return total + 1 + subtypes.length + packages;
  }, 0);

/**
 * Flatten the authenticated service tree into an id+name-only snapshot.
 * Entries with a blank name are dropped: they can't be selected on the public
 * form anyway, and the server rejects empty snapshot names, which would fail
 * the whole save.
 */
const toServiceOptionNodes = (
  services: ServiceType[],
): CustomerIntakeServiceOptionNode[] =>
  services
    .filter((serviceType) => serviceType.name?.trim())
    .map((serviceType) => ({
      id: String(serviceType.id),
      name: serviceType.name.trim(),
      subtypes: (serviceType.subtypes || [])
        .filter((subtype) => subtype.name?.trim())
        .map((subtype) => ({
          id: String(subtype.id),
          name: subtype.name.trim(),
          packages: (subtype.subsubtypes || [])
            .filter((pkg) => pkg.name?.trim())
            .map((pkg) => ({
              id: String(pkg.id),
              name: pkg.name.trim(),
            })),
        })),
    }));

/** Ids of every node kept in a service snapshot (all three levels). */
const selectedServiceIds = (
  options: CustomerIntakeServiceOptionNode[] = [],
): Set<string> => {
  const ids = new Set<string>();
  options.forEach((serviceType) => {
    ids.add(serviceType.id);
    (serviceType.subtypes || []).forEach((subtype) => {
      ids.add(subtype.id);
      (subtype.packages || []).forEach((pkg) => ids.add(pkg.id));
    });
  });
  return ids;
};

/**
 * Rebuild a snapshot from the live service tree, keeping only ids present in
 * `selected`. A branch is dropped entirely when its id is deselected, so
 * children can never outlive their parent.
 */
const pruneServiceTree = (
  tree: CustomerIntakeServiceOptionNode[],
  selected: Set<string>,
): CustomerIntakeServiceOptionNode[] =>
  tree
    .filter((serviceType) => selected.has(serviceType.id))
    .map((serviceType) => ({
      id: serviceType.id,
      name: serviceType.name,
      subtypes: (serviceType.subtypes || [])
        .filter((subtype) => selected.has(subtype.id))
        .map((subtype) => ({
          id: subtype.id,
          name: subtype.name,
          packages: (subtype.packages || [])
            .filter((pkg) => selected.has(pkg.id))
            .map((pkg) => ({ id: pkg.id, name: pkg.name })),
        })),
    }));

const subtypeFullySelected = (
  subtype: NonNullable<CustomerIntakeServiceOptionNode['subtypes']>[number],
  selected: Set<string>,
): boolean =>
  selected.has(subtype.id) &&
  (subtype.packages || []).every((pkg) => selected.has(pkg.id));

const serviceTypeFullySelected = (
  serviceType: CustomerIntakeServiceOptionNode,
  selected: Set<string>,
): boolean =>
  selected.has(serviceType.id) &&
  (serviceType.subtypes || []).every((subtype) =>
    subtypeFullySelected(subtype, selected),
  );

const CHOICE_FIELD_TYPES: CustomerIntakeFieldType[] = [
  'radio',
  'checkbox',
  'select',
];

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const createId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const isChoiceField = (type: CustomerIntakeFieldType) =>
  CHOICE_FIELD_TYPES.includes(type);

const BUILT_IN_SECTION_IDS = new Set(['basic-information', 'confirmation']);

export const isCustomerIntakeAddedSection = (
  section: CustomerIntakeSection,
  mode: CustomerIntakeEditorMode = 'form-version',
) =>
  !BUILT_IN_SECTION_IDS.has(section.id) &&
  !(mode === 'package' && section.id === 'package-information');

export const createCustomerIntakeField = (
  type: CustomerIntakeFieldType,
): CustomerIntakeField => {
  const field: CustomerIntakeField = {
    id: createId('field'),
    label: 'New question',
    type,
    required: false,
    placeholder: type === 'text' ? 'Enter your answer' : undefined,
  };

  if (type === 'consent') {
    field.label = 'I agree to provide this information.';
    field.required = true;
  }

  if (isChoiceField(type)) {
    field.options = [
      { value: createId('option'), label: 'Option 1' },
      { value: createId('option'), label: 'Option 2' },
    ];
  }

  if (type === 'service') {
    field.label = 'Which service are you interested in?';
    field.service = createDefaultServiceConfig();
  }

  return field;
};

const DEFAULT_PACKAGE_FORM_TITLE = 'Package information';
const DEFAULT_PACKAGE_SECTION_TITLE = 'Package Information';

const normalizePackageName = (packageName?: string) =>
  packageName?.trim() || '';

export const createEmptyPackageFormConfiguration = (
  packageName?: string,
): CustomerIntakeFormConfig => {
  const normalizedPackageName = normalizePackageName(packageName);

  return {
    title: normalizedPackageName || DEFAULT_PACKAGE_FORM_TITLE,
    description: '',
    estimatedMinutes: 3,
    sections: [
      {
        id: 'package-information',
        title: normalizedPackageName
          ? `${normalizedPackageName} - Information`
          : DEFAULT_PACKAGE_SECTION_TITLE,
        description:
          'Please provide the information required for this package.',
        fields: [],
      },
    ],
  };
};

/**
 * Applies package-name defaults without replacing titles that were customized
 * by the user. This also upgrades configurations saved with the old generic
 * defaults when a package is edited.
 */
export const applyPackageNameDefaults = (
  configuration: CustomerIntakeFormConfig,
  packageName?: string,
  previousPackageName?: string,
): CustomerIntakeFormConfig => {
  const nextDefaults = createEmptyPackageFormConfiguration(packageName);
  const genericDefaults = createEmptyPackageFormConfiguration();
  const previousDefaults =
    createEmptyPackageFormConfiguration(previousPackageName);

  const isDefaultValue = (value: string | undefined, defaults: string[]) => {
    const normalizedValue = value?.trim() || '';
    return (
      !normalizedValue ||
      defaults.some((defaultValue) => defaultValue === normalizedValue)
    );
  };

  const sections = configuration.sections.length
    ? [...configuration.sections]
    : [nextDefaults.sections[0]];
  const firstSection = sections[0];

  return {
    ...configuration,
    title: isDefaultValue(configuration.title, [
      genericDefaults.title,
      previousDefaults.title,
    ])
      ? nextDefaults.title
      : configuration.title,
    sections: [
      {
        ...firstSection,
        title: isDefaultValue(firstSection.title, [
          genericDefaults.sections[0].title,
          previousDefaults.sections[0].title,
        ])
          ? nextDefaults.sections[0].title
          : firstSection.title,
      },
      ...sections.slice(1),
    ],
  };
};

const nextUniqueId = (prefix: string, usedIds: Set<string>) => {
  let id = createId(prefix);
  while (usedIds.has(id)) id = createId(prefix);
  usedIds.add(id);
  return id;
};

export const namespaceImportedDefinition = (
  imported: CustomerIntakeFormConfig,
  packageId: string | number,
  reservedSectionIds: Set<string> = new Set<string>(),
  reservedFieldIds: Set<string> = new Set<string>(),
): CustomerIntakeFormConfig => {
  const sectionIds = new Set(reservedSectionIds);
  const fieldIds = new Set(reservedFieldIds);
  const fieldIdMap = new Map<string, string>();
  const sections = clone(imported.sections).map((section) => {
    const nextSectionId = nextUniqueId(`pkg-${packageId}-section`, sectionIds);

    const fields = section.fields.map((field) => {
      const nextFieldId = nextUniqueId(`pkg-${packageId}-field`, fieldIds);
      fieldIdMap.set(field.id, nextFieldId);
      return { ...field, id: nextFieldId };
    });

    return { ...section, id: nextSectionId, fields };
  });

  return {
    ...clone(imported),
    sections: sections.map((section) => ({
      ...section,
      fields: section.fields.map((field) => ({
        ...field,
        conditional: field.conditional
          ? {
              ...field.conditional,
              fieldId:
                fieldIdMap.get(field.conditional.fieldId) ||
                field.conditional.fieldId,
            }
          : undefined,
      })),
    })),
  };
};

export const importPackageConfigurationIntoDefinition = (
  current: CustomerIntakeFormConfig,
  imported: CustomerIntakeFormConfig,
  packageId: string | number,
  target?: CustomerIntakeCustomTypeTarget,
  serviceBinding?: CustomerIntakeServiceBinding,
): CustomerIntakeFormConfig => {
  if (
    imported.sections.some((section) =>
      section.fields.some((field) => field.type === 'custom'),
    )
  ) {
    throw new Error(
      'This Package configuration contains an unsupported nested Custom type.',
    );
  }

  const nextCurrent = clone(current);
  if (target?.fieldId) {
    nextCurrent.sections = nextCurrent.sections.map((section) => ({
      ...section,
      fields: section.fields.filter((field) => field.id !== target.fieldId),
    }));
  }

  const reservedSectionIds = new Set(
    nextCurrent.sections.map((section) => section.id),
  );
  const reservedFieldIds = new Set(
    nextCurrent.sections.flatMap((section) =>
      section.fields.map((field) => field.id),
    ),
  );
  const namespaced = namespaceImportedDefinition(
    imported,
    packageId,
    reservedSectionIds,
    reservedFieldIds,
  );
  const confirmationIndex = nextCurrent.sections.findIndex(
    (section) =>
      section.id === 'confirmation' ||
      section.fields.some((field) => field.type === 'consent'),
  );
  const insertAt =
    confirmationIndex >= 0 ? confirmationIndex : nextCurrent.sections.length;

  nextCurrent.sections.splice(insertAt, 0, ...namespaced.sections);

  if (serviceBinding) {
    const packageIdsInSections = new Set(
      nextCurrent.sections
        .map((section) => section.id.match(/^pkg-(\d+)-/)?.[1])
        .filter((packageId): packageId is string => Boolean(packageId)),
    );
    const bindings = (nextCurrent.serviceBindings || []).filter((binding) =>
      packageIdsInSections.has(String(binding.packageId)),
    );
    nextCurrent.serviceBindings = [
      ...bindings.filter(
        (binding) => binding.packageId !== serviceBinding.packageId,
      ),
      serviceBinding,
    ];
  }

  return nextCurrent;
};

type ServiceAllowListPickerProps = {
  tree: CustomerIntakeServiceOptionNode[];
  options?: CustomerIntakeServiceOptionNode[];
  disabled?: boolean;
  onToggleType: (
    serviceType: CustomerIntakeServiceOptionNode,
    checked: boolean,
  ) => void;
  onToggleSubtype: (
    serviceType: CustomerIntakeServiceOptionNode,
    subtype: NonNullable<CustomerIntakeServiceOptionNode['subtypes']>[number],
    checked: boolean,
  ) => void;
  onTogglePackage: (
    serviceType: CustomerIntakeServiceOptionNode,
    subtype: NonNullable<CustomerIntakeServiceOptionNode['subtypes']>[number],
    pkg: { id: string; name: string },
    checked: boolean,
  ) => void;
  onSelectAll: (selectAll: boolean) => void;
};

/**
 * Checkbox tree over the live service catalog. Ticking a subset limits what the
 * public form shows; a parent checkbox cascades to its descendants and reflects
 * an indeterminate state when only some children are ticked.
 */
const ServiceAllowListPicker = ({
  tree,
  options,
  disabled = false,
  onToggleType,
  onToggleSubtype,
  onTogglePackage,
  onSelectAll,
}: ServiceAllowListPickerProps) => {
  const selected = selectedServiceIds(options);

  // Expand/collapse state. Service types are expanded by default (tracked via a
  // collapsed set), while subtype package lists are collapsed by default
  // (tracked via an expanded set) to keep the long catalog compact.
  const [collapsedTypes, setCollapsedTypes] = useState<Set<string>>(new Set());
  const [expandedSubtypes, setExpandedSubtypes] = useState<Set<string>>(
    new Set(),
  );

  const toggleType = (id: string) =>
    setCollapsedTypes((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSubtype = (id: string) =>
    setExpandedSubtypes((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const expandAll = () => {
    setCollapsedTypes(new Set());
    setExpandedSubtypes(
      new Set(
        tree.flatMap((serviceType) =>
          (serviceType.subtypes || []).map((subtype) => subtype.id),
        ),
      ),
    );
  };

  const collapseAll = () => {
    setCollapsedTypes(new Set(tree.map((serviceType) => serviceType.id)));
    setExpandedSubtypes(new Set());
  };

  return (
    <Box
      sx={{
        mt: 1,
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}
      >
        <Button
          type="button"
          size="small"
          variant="text"
          disabled={disabled}
          onClick={() => onSelectAll(true)}
        >
          Select all
        </Button>
        <Button
          type="button"
          size="small"
          variant="text"
          color="inherit"
          disabled={disabled}
          onClick={() => onSelectAll(false)}
        >
          Clear all
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          type="button"
          size="small"
          variant="text"
          color="inherit"
          startIcon={<ExpandMore />}
          onClick={expandAll}
        >
          Expand all
        </Button>
        <Button
          type="button"
          size="small"
          variant="text"
          color="inherit"
          startIcon={<ExpandLess />}
          onClick={collapseAll}
        >
          Collapse all
        </Button>
      </Stack>
      {tree.map((serviceType) => {
        const typeChecked = serviceTypeFullySelected(serviceType, selected);
        const typeIndeterminate = !typeChecked && selected.has(serviceType.id);
        const typeExpanded = !collapsedTypes.has(serviceType.id);
        const subtypes = serviceType.subtypes || [];
        return (
          <Box key={serviceType.id}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                size="small"
                aria-label={typeExpanded ? 'Collapse' : 'Expand'}
                onClick={() => toggleType(serviceType.id)}
                sx={{ mr: 0.5, visibility: subtypes.length ? 'visible' : 'hidden' }}
              >
                {typeExpanded ? (
                  <ExpandLess fontSize="small" />
                ) : (
                  <ExpandMore fontSize="small" />
                )}
              </IconButton>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={typeChecked}
                    indeterminate={typeIndeterminate}
                    disabled={disabled}
                    onChange={(event) =>
                      onToggleType(serviceType, event.target.checked)
                    }
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={700}>
                    {serviceType.name}
                  </Typography>
                }
              />
            </Box>
            <Collapse in={typeExpanded} timeout="auto" unmountOnExit>
              {subtypes.map((subtype) => {
                const subChecked = subtypeFullySelected(subtype, selected);
                const subIndeterminate =
                  !subChecked && selected.has(subtype.id);
                const subExpanded = expandedSubtypes.has(subtype.id);
                const packages = subtype.packages || [];
                return (
                  <Box key={subtype.id} sx={{ pl: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        aria-label={subExpanded ? 'Collapse' : 'Expand'}
                        onClick={() => toggleSubtype(subtype.id)}
                        sx={{
                          mr: 0.5,
                          visibility: packages.length ? 'visible' : 'hidden',
                        }}
                      >
                        {subExpanded ? (
                          <ExpandLess fontSize="small" />
                        ) : (
                          <ExpandMore fontSize="small" />
                        )}
                      </IconButton>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={subChecked}
                            indeterminate={subIndeterminate}
                            disabled={disabled}
                            onChange={(event) =>
                              onToggleSubtype(
                                serviceType,
                                subtype,
                                event.target.checked,
                              )
                            }
                          />
                        }
                        label={
                          <Typography variant="body2" fontWeight={600}>
                            {subtype.name}
                          </Typography>
                        }
                      />
                    </Box>
                    <Collapse in={subExpanded} timeout="auto" unmountOnExit>
                      {packages.map((pkg) => (
                        <Box key={pkg.id} sx={{ pl: 6 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={selected.has(pkg.id)}
                                disabled={disabled}
                                onChange={(event) =>
                                  onTogglePackage(
                                    serviceType,
                                    subtype,
                                    pkg,
                                    event.target.checked,
                                  )
                                }
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {pkg.name}
                              </Typography>
                            }
                          />
                        </Box>
                      ))}
                    </Collapse>
                  </Box>
                );
              })}
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

type CustomerIntakeDefinitionEditorProps = {
  value: CustomerIntakeFormConfig;
  onChange: (value: CustomerIntakeFormConfig) => void;
  mode?: CustomerIntakeEditorMode;
  onAddCustomType?: () => void;
  disabled?: boolean;
};

const CustomerIntakeDefinitionEditor = ({
  value,
  onChange,
  mode = 'form-version',
  onAddCustomType,
  disabled = false,
}: CustomerIntakeDefinitionEditorProps) => {
  // Custom package configurations are imported through the dedicated action
  // below. They are never a field type in either editor.
  const fieldTypeOptions = CUSTOMER_INTAKE_FIELD_TYPE_OPTIONS;
  const [sectionDeleteDialogOpen, setSectionDeleteDialogOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] =
    useState<CustomerIntakeSection | null>(null);
  const [addFieldMenu, setAddFieldMenu] = useState<{
    anchorEl: HTMLElement;
    sectionId: string;
  } | null>(null);

  const closeAddFieldMenu = () => setAddFieldMenu(null);

  // Service fields snapshot the live service tree into the definition so the
  // public form can render choices without an authenticated lookup.
  const [serviceTree, setServiceTree] = useState<
    CustomerIntakeServiceOptionNode[]
  >([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState('');
  // Which service fields have their "limit services" picker expanded.
  const [openServicePickers, setOpenServicePickers] = useState<Set<string>>(
    () => new Set(),
  );

  const toggleServicePicker = (fieldId: string) =>
    setOpenServicePickers((current) => {
      const next = new Set(current);
      if (next.has(fieldId)) next.delete(fieldId);
      else next.add(fieldId);
      return next;
    });

  useEffect(() => {
    let active = true;
    setServicesLoading(true);
    listServiceTree()
      .then((services) => {
        if (!active) return;
        setServiceTree(toServiceOptionNodes(services));
        setServicesError('');
      })
      .catch(() => {
        if (active) setServicesError('Unable to load the service list.');
      })
      .finally(() => {
        if (active) setServicesLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const updateSection = (
    sectionId: string,
    update: Partial<CustomerIntakeSection>,
  ) => {
    onChange({
      ...value,
      sections: value.sections.map((section) =>
        section.id === sectionId ? { ...section, ...update } : section,
      ),
    });
  };

  const updateField = (
    sectionId: string,
    fieldId: string,
    update: Partial<CustomerIntakeField>,
  ) => {
    onChange({
      ...value,
      sections: value.sections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.map((field) =>
                field.id === fieldId ? { ...field, ...update } : field,
              ),
            }
          : section,
      ),
    });
  };

  const changeFieldType = (
    sectionId: string,
    field: CustomerIntakeField,
    type: CustomerIntakeFieldType,
  ) => {
    updateField(sectionId, field.id, {
      type,
      options: isChoiceField(type)
        ? field.options?.length
          ? field.options
          : createCustomerIntakeField(type).options
        : undefined,
      placeholder:
        type === 'text' ? field.placeholder || 'Enter your answer' : undefined,
      service:
        type === 'service'
          ? (field.service ?? createDefaultServiceConfig(serviceTree))
          : undefined,
    });
  };

  const addField = (sectionId: string, type: CustomerIntakeFieldType) => {
    const newField = createCustomerIntakeField(type);
    if (newField.type === 'service' && newField.service) {
      newField.service = { ...newField.service, options: serviceTree };
    }
    updateSection(sectionId, {
      fields: [
        ...(value.sections.find((section) => section.id === sectionId)
          ?.fields || []),
        newField,
      ],
    });
  };

  const updateFieldService = (
    sectionId: string,
    field: CustomerIntakeField,
    update: Partial<CustomerIntakeServiceConfig>,
  ) => {
    const current = field.service ?? createDefaultServiceConfig();
    updateField(sectionId, field.id, { service: { ...current, ...update } });
  };

  const syncFieldServiceOptions = (
    sectionId: string,
    field: CustomerIntakeField,
  ) => updateFieldService(sectionId, field, { options: serviceTree });

  // Persist a service allow-list by pruning the live tree to `selected`.
  const applyServiceSelection = (
    sectionId: string,
    field: CustomerIntakeField,
    selected: Set<string>,
  ) =>
    updateFieldService(sectionId, field, {
      options: pruneServiceTree(serviceTree, selected),
    });

  const setAllServicesSelected = (
    sectionId: string,
    field: CustomerIntakeField,
    selectAll: boolean,
  ) =>
    updateFieldService(sectionId, field, {
      options: selectAll ? serviceTree : [],
    });

  const toggleServiceTypeSelected = (
    sectionId: string,
    field: CustomerIntakeField,
    serviceType: CustomerIntakeServiceOptionNode,
    checked: boolean,
  ) => {
    const selected = selectedServiceIds(field.service?.options);
    const ids = [
      serviceType.id,
      ...(serviceType.subtypes || []).flatMap((subtype) => [
        subtype.id,
        ...(subtype.packages || []).map((pkg) => pkg.id),
      ]),
    ];
    ids.forEach((id) => (checked ? selected.add(id) : selected.delete(id)));
    applyServiceSelection(sectionId, field, selected);
  };

  const toggleServiceSubtypeSelected = (
    sectionId: string,
    field: CustomerIntakeField,
    serviceType: CustomerIntakeServiceOptionNode,
    subtype: NonNullable<CustomerIntakeServiceOptionNode['subtypes']>[number],
    checked: boolean,
  ) => {
    const selected = selectedServiceIds(field.service?.options);
    const ids = [subtype.id, ...(subtype.packages || []).map((pkg) => pkg.id)];
    if (checked) {
      selected.add(serviceType.id);
      ids.forEach((id) => selected.add(id));
    } else {
      ids.forEach((id) => selected.delete(id));
    }
    applyServiceSelection(sectionId, field, selected);
  };

  const toggleServicePackageSelected = (
    sectionId: string,
    field: CustomerIntakeField,
    serviceType: CustomerIntakeServiceOptionNode,
    subtype: NonNullable<CustomerIntakeServiceOptionNode['subtypes']>[number],
    pkg: { id: string; name: string },
    checked: boolean,
  ) => {
    const selected = selectedServiceIds(field.service?.options);
    if (checked) {
      selected.add(serviceType.id);
      selected.add(subtype.id);
      selected.add(pkg.id);
    } else {
      selected.delete(pkg.id);
    }
    applyServiceSelection(sectionId, field, selected);
  };

  const updateOption = (
    sectionId: string,
    field: CustomerIntakeField,
    optionId: string,
    label: string,
  ) => {
    updateField(sectionId, field.id, {
      options: (field.options || []).map((option) =>
        option.value === optionId ? { ...option, label } : option,
      ),
    });
  };

  const addOption = (sectionId: string, field: CustomerIntakeField) => {
    updateField(sectionId, field.id, {
      options: [
        ...(field.options || []),
        {
          value: createId('option'),
          label: `Option ${(field.options || []).length + 1}`,
        },
      ],
    });
  };

  const removeOption = (
    sectionId: string,
    field: CustomerIntakeField,
    optionId: string,
  ) => {
    updateField(sectionId, field.id, {
      options: (field.options || []).filter(
        (option) => option.value !== optionId,
      ),
    });
  };

  const removeField = (sectionId: string, fieldId: string) => {
    const section = value.sections.find((item) => item.id === sectionId);
    if (!section) return;
    updateSection(sectionId, {
      fields: section.fields.filter((field) => field.id !== fieldId),
    });
  };

  const duplicateField = (sectionId: string, field: CustomerIntakeField) => {
    const section = value.sections.find((item) => item.id === sectionId);
    if (!section) return;
    const duplicatedField = {
      ...clone(field),
      id: createId('field'),
      label: `${field.label} copy`,
    };
    updateSection(sectionId, {
      fields: section.fields.flatMap((currentField) =>
        currentField.id === field.id
          ? [currentField, duplicatedField]
          : [currentField],
      ),
    });
  };

  const moveField = (
    sectionId: string,
    fieldId: string,
    direction: 'up' | 'down',
  ) => {
    const section = value.sections.find((item) => item.id === sectionId);
    if (!section) return;
    const currentIndex = section.fields.findIndex(
      (field) => field.id === fieldId,
    );
    const targetIndex =
      direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (
      currentIndex < 0 ||
      targetIndex < 0 ||
      targetIndex >= section.fields.length
    ) {
      return;
    }

    const fields = [...section.fields];
    [fields[currentIndex], fields[targetIndex]] = [
      fields[targetIndex],
      fields[currentIndex],
    ];
    updateSection(sectionId, { fields });
  };

  const addSection = () => {
    const newSection: CustomerIntakeSection = {
      id: createId('section'),
      title: 'New section',
      description: 'Add a short description for this section.',
      fields: [],
    };
    const sections = [...value.sections];
    const confirmationIndex = sections.findIndex(
      (section) =>
        section.id === 'confirmation' ||
        section.fields.some((field) => field.type === 'consent'),
    );
    sections.splice(
      confirmationIndex >= 0 ? confirmationIndex : sections.length,
      0,
      newSection,
    );
    onChange({ ...value, sections });
  };

  const requestSectionDelete = (section: CustomerIntakeSection) => {
    if (disabled || !isCustomerIntakeAddedSection(section, mode)) return;
    setSectionToDelete(section);
    setSectionDeleteDialogOpen(true);
  };

  const closeSectionDeleteDialog = () => {
    setSectionDeleteDialogOpen(false);
    setSectionToDelete(null);
  };

  const deleteSelectedSection = () => {
    if (
      !sectionToDelete ||
      !isCustomerIntakeAddedSection(sectionToDelete, mode)
    ) {
      closeSectionDeleteDialog();
      return;
    }

    onChange({
      ...value,
      sections: value.sections.filter(
        (section) => section.id !== sectionToDelete.id,
      ),
    });
    closeSectionDeleteDialog();
  };

  return (
    <Stack spacing={1.5}>
      <Card
        variant="outlined"
        sx={{
          p: 1.5,
          borderColor: 'primary.main',
          bgcolor: 'rgba(77, 167, 219, 0.08)',
        }}
      >
        <Stack spacing={1.25}>
          <Typography
            variant="overline"
            fontWeight={700}
            color="primary.main"
            lineHeight={1.4}
          >
            Form header
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Form title"
            value={value.title}
            disabled={disabled}
            sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
            onChange={(event) =>
              onChange({ ...value, title: event.target.value })
            }
          />
          <TextField
            fullWidth
            size="small"
            label="Form description"
            value={value.description}
            disabled={disabled}
            sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
            onChange={(event) =>
              onChange({ ...value, description: event.target.value })
            }
          />
        </Stack>
      </Card>

      {value.sections.map((section, sectionIndex) => (
        <Box key={section.id} component="section">
          {sectionIndex > 0 && (
            <Divider
              sx={{
                width: '100%',
                mb: 1.5,
                borderBottomWidth: 2,
                borderColor: 'grey.400',
              }}
            />
          )}
          <Box sx={{ py: 1 }}>
            <Stack spacing={1.25}>
              <Card
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(77, 167, 219, 0.08)',
                }}
              >
                <Stack spacing={1.25}>
                  <Typography
                    variant="overline"
                    fontWeight={700}
                    color="primary.main"
                    lineHeight={1.4}
                  >
                    Section
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.5}
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        label="Section title"
                        value={section.title}
                        disabled={disabled}
                        sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                        onChange={(event) =>
                          updateSection(section.id, {
                            title: event.target.value,
                          })
                        }
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Section description"
                        value={section.description || ''}
                        disabled={disabled}
                        sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                        onChange={(event) =>
                          updateSection(section.id, {
                            description: event.target.value,
                          })
                        }
                      />
                    </Stack>
                    {isCustomerIntakeAddedSection(section, mode) && (
                      <Tooltip title="Delete section">
                        <span>
                          <IconButton
                            type="button"
                            color="error"
                            disabled={disabled}
                            aria-label={`Delete section ${section.title}`}
                            onClick={() => requestSectionDelete(section)}
                          >
                            <DeleteOutline />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>
              </Card>

              {section.fields.map((field, fieldIndex) => {
                const selectedOption = CUSTOMER_INTAKE_FIELD_TYPE_OPTIONS.find(
                  (option) => option.value === field.type,
                );
                const FieldTypeIcon = selectedOption?.Icon;

                return (
                  <Card
                    key={field.id}
                    variant="outlined"
                    sx={{ p: 1.5, bgcolor: 'action.hover' }}
                  >
                    <Stack spacing={1.25}>
                      <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', lg: 'center' }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ flex: 1, minWidth: 0 }}
                        >
                          <Box
                            sx={{
                              width: 26,
                              height: 26,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              borderRadius: '50%',
                              backgroundColor: 'background.paper',
                              border: '1.5px solid',
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              fontWeight: 700,
                              fontSize: '0.8125rem',
                              lineHeight: 1,
                            }}
                          >
                            {fieldIndex + 1}
                          </Box>
                          <TextField
                            fullWidth
                            size="small"
                            label="Question / field label"
                            value={field.label}
                            disabled={disabled}
                            onChange={(event) =>
                              updateField(section.id, field.id, {
                                label: event.target.value,
                              })
                            }
                          />
                        </Stack>
                        <FormControl
                          size="small"
                          sx={{ minWidth: { lg: 210 } }}
                          disabled={disabled}
                        >
                          <InputLabel id={`${field.id}-type-label`}>
                            Field type
                          </InputLabel>
                          <Select
                            labelId={`${field.id}-type-label`}
                            label="Field type"
                            value={field.type}
                            renderValue={() =>
                              FieldTypeIcon ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <FieldTypeIcon fontSize="small" />
                                  <span>{selectedOption?.label}</span>
                                </Stack>
                              ) : (
                                field.type
                              )
                            }
                            onChange={(event) =>
                              changeFieldType(
                                section.id,
                                field,
                                event.target.value as CustomerIntakeFieldType,
                              )
                            }
                          >
                            {fieldTypeOptions.map((option) => {
                              const OptionIcon = option.Icon;
                              return (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <OptionIcon fontSize="small" />
                                    <span>{option.label}</span>
                                  </Stack>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(field.required)}
                              disabled={disabled}
                              onChange={(event) =>
                                updateField(section.id, field.id, {
                                  required: event.target.checked,
                                })
                              }
                            />
                          }
                          label="Required"
                        />
                      </Stack>

                      {isChoiceField(field.type) && (
                        <Stack spacing={1}>
                          <Typography variant="subtitle2" fontWeight={700}>
                            Options
                          </Typography>
                          {(field.options || []).map(
                            (option: CustomerIntakeFieldOption) => (
                              <Stack
                                key={option.value}
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={option.label}
                                  disabled={disabled}
                                  onChange={(event) =>
                                    updateOption(
                                      section.id,
                                      field,
                                      option.value,
                                      event.target.value,
                                    )
                                  }
                                  aria-label={`Option for ${field.label}`}
                                />
                                <IconButton
                                  type="button"
                                  aria-label="Remove option"
                                  color="error"
                                  disabled={disabled}
                                  onClick={() =>
                                    removeOption(
                                      section.id,
                                      field,
                                      option.value,
                                    )
                                  }
                                >
                                  <DeleteOutline />
                                </IconButton>
                              </Stack>
                            ),
                          )}
                          <Button
                            type="button"
                            size="small"
                            variant="text"
                            startIcon={<Add />}
                            disabled={disabled}
                            onClick={() => addOption(section.id, field)}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            Add option
                          </Button>
                        </Stack>
                      )}

                      {field.type === 'service' && (
                        <Stack spacing={1.25}>
                          <Typography variant="subtitle2" fontWeight={700}>
                            Service selection
                          </Typography>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.5}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                          >
                            <FormControl
                              size="small"
                              sx={{ minWidth: 240 }}
                              disabled={disabled}
                            >
                              <InputLabel id={`${field.id}-service-mode`}>
                                Selection scope
                              </InputLabel>
                              <Select
                                labelId={`${field.id}-service-mode`}
                                label="Selection scope"
                                value={field.service?.mode || 'full'}
                                onChange={(event) => {
                                  const mode = event.target.value as
                                    'full' | 'single';
                                  updateFieldService(section.id, field, {
                                    mode,
                                    // Single-level fields must carry a level;
                                    // the picker only *displays* a default, so
                                    // persist it here or the save is rejected.
                                    level:
                                      mode === 'single'
                                        ? (field.service?.level ??
                                          'serviceType')
                                        : undefined,
                                  });
                                }}
                              >
                                <MenuItem value="full">
                                  All 3 levels (Type → Sub-type → Service)
                                </MenuItem>
                                <MenuItem value="single">Single level</MenuItem>
                              </Select>
                            </FormControl>
                            {field.service?.mode === 'single' && (
                              <FormControl
                                size="small"
                                sx={{ minWidth: 180 }}
                                disabled={disabled}
                              >
                                <InputLabel id={`${field.id}-service-level`}>
                                  Level
                                </InputLabel>
                                <Select
                                  labelId={`${field.id}-service-level`}
                                  label="Level"
                                  value={field.service?.level || 'serviceType'}
                                  onChange={(event) =>
                                    updateFieldService(section.id, field, {
                                      level: event.target
                                        .value as CustomerIntakeServiceLevel,
                                    })
                                  }
                                >
                                  {CUSTOMER_INTAKE_SERVICE_LEVEL_OPTIONS.map(
                                    (option) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </MenuItem>
                                    ),
                                  )}
                                </Select>
                              </FormControl>
                            )}
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={Boolean(field.service?.multiple)}
                                  disabled={disabled}
                                  onChange={(event) =>
                                    updateFieldService(section.id, field, {
                                      multiple: event.target.checked,
                                    })
                                  }
                                />
                              }
                              label="Allow multiple"
                            />
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                          >
                            <Typography
                              variant="caption"
                              color={servicesError ? 'error' : 'text.secondary'}
                            >
                              {servicesError
                                ? servicesError
                                : `${countServiceOptionNodes(
                                    field.service?.options,
                                  )} of ${countServiceOptionNodes(
                                    serviceTree,
                                  )} service option(s) shown on the form.`}
                            </Typography>
                            <Button
                              type="button"
                              size="small"
                              variant="text"
                              startIcon={<Sync />}
                              disabled={
                                disabled ||
                                servicesLoading ||
                                serviceTree.length === 0
                              }
                              onClick={() =>
                                syncFieldServiceOptions(section.id, field)
                              }
                            >
                              {servicesLoading
                                ? 'Loading services…'
                                : 'Sync services'}
                            </Button>
                            <Button
                              type="button"
                              size="small"
                              variant="text"
                              startIcon={<FilterList />}
                              endIcon={
                                openServicePickers.has(field.id) ? (
                                  <ExpandLess />
                                ) : (
                                  <ExpandMore />
                                )
                              }
                              disabled={
                                disabled ||
                                servicesLoading ||
                                serviceTree.length === 0
                              }
                              onClick={() => toggleServicePicker(field.id)}
                            >
                              Limit services shown
                            </Button>
                          </Stack>
                          <Collapse
                            in={openServicePickers.has(field.id)}
                            unmountOnExit
                          >
                            <ServiceAllowListPicker
                              tree={serviceTree}
                              options={field.service?.options}
                              disabled={disabled}
                              onSelectAll={(selectAll) =>
                                setAllServicesSelected(
                                  section.id,
                                  field,
                                  selectAll,
                                )
                              }
                              onToggleType={(serviceType, checked) =>
                                toggleServiceTypeSelected(
                                  section.id,
                                  field,
                                  serviceType,
                                  checked,
                                )
                              }
                              onToggleSubtype={(
                                serviceType,
                                subtype,
                                checked,
                              ) =>
                                toggleServiceSubtypeSelected(
                                  section.id,
                                  field,
                                  serviceType,
                                  subtype,
                                  checked,
                                )
                              }
                              onTogglePackage={(
                                serviceType,
                                subtype,
                                pkg,
                                checked,
                              ) =>
                                toggleServicePackageSelected(
                                  section.id,
                                  field,
                                  serviceType,
                                  subtype,
                                  pkg,
                                  checked,
                                )
                              }
                            />
                          </Collapse>
                        </Stack>
                      )}

                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1}
                      >
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Move up">
                            <span>
                              <IconButton
                                type="button"
                                size="small"
                                disabled={disabled || fieldIndex === 0}
                                onClick={() =>
                                  moveField(section.id, field.id, 'up')
                                }
                              >
                                <ArrowUpward fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move down">
                            <span>
                              <IconButton
                                type="button"
                                size="small"
                                disabled={
                                  disabled ||
                                  fieldIndex === section.fields.length - 1
                                }
                                onClick={() =>
                                  moveField(section.id, field.id, 'down')
                                }
                              >
                                <ArrowDownward fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Duplicate field">
                            <span>
                              <IconButton
                                type="button"
                                size="small"
                                disabled={disabled}
                                onClick={() =>
                                  duplicateField(section.id, field)
                                }
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete field">
                            <span>
                              <IconButton
                                type="button"
                                size="small"
                                color="error"
                                disabled={disabled}
                                onClick={() =>
                                  removeField(section.id, field.id)
                                }
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Card>
                );
              })}

              <Box>
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<AddCircleOutline />}
                  disabled={disabled}
                  onClick={(event) =>
                    setAddFieldMenu({
                      anchorEl: event.currentTarget,
                      sectionId: section.id,
                    })
                  }
                >
                  Add field
                </Button>
              </Box>
            </Stack>
          </Box>
        </Box>
      ))}

      <Menu
        anchorEl={addFieldMenu?.anchorEl ?? null}
        open={Boolean(addFieldMenu)}
        onClose={closeAddFieldMenu}
      >
        {fieldTypeOptions.map((option) => {
          const OptionIcon = option.Icon;
          return (
            <MenuItem
              key={option.value}
              onClick={() => {
                if (addFieldMenu)
                  addField(addFieldMenu.sectionId, option.value);
                closeAddFieldMenu();
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <OptionIcon fontSize="small" />
                <span>{option.label}</span>
              </Stack>
            </MenuItem>
          );
        })}
      </Menu>

      <Stack direction="row" spacing={1} justifyContent="center">
        <Button
          type="button"
          variant="outlined"
          startIcon={<AddCircleOutline />}
          disabled={disabled}
          onClick={addSection}
        >
          Add section
        </Button>
        {mode === 'form-version' && onAddCustomType && (
          <Button
            type="button"
            variant="outlined"
            startIcon={<Tune />}
            disabled={disabled}
            onClick={onAddCustomType}
          >
            Add custom type
          </Button>
        )}
      </Stack>

      <Dialog
        open={sectionDeleteDialogOpen}
        onClose={closeSectionDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete section?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete the selected section:{' '}
            <strong>{sectionToDelete?.title || 'this section'}</strong>? All
            fields inside it will also be deleted. The change will be applied
            when you save changes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSectionDeleteDialog}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={deleteSelectedSection}
          >
            Delete section
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default CustomerIntakeDefinitionEditor;
